import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { PersonIcon, TrashIcon } from '@radix-ui/react-icons';
import { Resend, Revoke, TimeIcon } from '@producer/ui-kit';

import { DropdownMenu, Item } from '../../../components/modals/dropdown-menu/DropdownMenu';
import { LimitReachedModal } from '../../../components/dialogs/limit-reached.modal';
import { UpgradeIcon } from '../../../components/icons/upgrade-icon';
import { timeParser } from '../../../lib/utils/time-parser';
import { catchError } from '../../../core/catch-error';
import { toastPool } from '../../toasts/models/toast-pool';
import { Toast } from '../../toasts/models/toast';
import { ChangeRoleModal } from './change-role.modal';
import { PromoteMemberModal } from './promote-member.modal';
import { RemoveMemberModal } from './remove-member.modal';
import styles from './member-row-actions.module.scss';

import type { Member } from '../../../app/entities/member';

type MemberRowActionsProps = {
  member: Member;
};

const RESEND_COOL_DOWN_SECONDS = 59;

/**
 * Owns the row's kebab menu and every modal it can open. Which items show up depends
 * on the member's role and status: a pending member is an invitation and gets the
 * resend/revoke pair, an active one gets the role and access actions.
 */
export const MemberRowActions: React.FC<MemberRowActionsProps> = observer(({ member }) => {
  const [isChangeRoleModalOpen, setIsChangeRoleModalOpen] = useState(false);
  const [isPromoteModalOpen, setIsPromoteModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [isLimitModalOpen, setIsLimitModalOpen] = useState(false);

  const [resendCoolDown, setResendCoolDown] = useState<number>(RESEND_COOL_DOWN_SECONDS);
  const [resentAt, setResentAt] = useState<null | number>(null);
  const [isResendDisabled, setIsResendDisabled] = useState<boolean>(false);

  const role = member.role?.toLowerCase();
  const isOwner = role === 'owner';
  const isGuest = role === 'guest';
  const isPending = member.status === 'pending';

  useEffect(() => {
    if (!resentAt || resendCoolDown < 1) {
      return;
    }

    const now = new Date();
    let lastInviteTimeDiff = Math.trunc(Math.abs(now.getTime() - resentAt) / 1000);

    const resendCoolDownInterval = setInterval(() => {
      if (lastInviteTimeDiff >= RESEND_COOL_DOWN_SECONDS) {
        clearInterval(resendCoolDownInterval);
        setIsResendDisabled(false);
      }

      const timeLeft = RESEND_COOL_DOWN_SECONDS - lastInviteTimeDiff;
      setResendCoolDown(timeLeft);
      lastInviteTimeDiff += 1;
    }, 1000);

    return () => clearInterval(resendCoolDownInterval);
  }, [resentAt, isResendDisabled, resendCoolDown]);

  const handlePromoteClicked = () => {
    if (member.space.canInviteMember()) {
      setIsPromoteModalOpen(true);
    } else {
      setIsLimitModalOpen(true);
    }
  };

  const handleResendInvitation = async () => {
    try {
      if (isResendDisabled) {
        return;
      }

      setResentAt(new Date().getTime());
      setIsResendDisabled(true);

      await member.resend();

      toastPool.insert(new Toast('Invitation sent.'));
    } catch (e) {
      catchError(e);
      setIsResendDisabled(false);
    }
  };

  const handleRevokeInvitation = async () => {
    try {
      await member.delete();
    } catch (e) {
      catchError(e);
    }
  };

  const menuItems: Item[] = [];

  // The owner's role is fixed and their access cannot be revoked.
  if (!isOwner) {
    // A guest's role changes through the seat-checked promote flow, not through the modal.
    if (!isGuest) {
      menuItems.push({
        title: 'Change role...',
        onSelect: () => setIsChangeRoleModalOpen(true),
        icon: <PersonIcon />,
      });
    }

    if (isGuest && !isPending) {
      menuItems.push({
        title: 'Promote to creator',
        onSelect: handlePromoteClicked,
        icon: <UpgradeIcon />,
      });
    }

    if (isPending) {
      menuItems.push(
        {
          title: 'Resend invitation',
          icon: <Resend />,
          disabled: isResendDisabled,
          onSelect: handleResendInvitation,
        },
        {
          title: 'Revoke invitation',
          icon: <Revoke />,
          type: 'danger' as const,
          onSelect: handleRevokeInvitation,
        },
      );
    } else {
      menuItems.push({
        title: 'Revoke access',
        onSelect: () => setIsRemoveModalOpen(true),
        type: 'danger' as const,
        icon: <TrashIcon />,
      });
    }
  }

  return (
    <div className={styles.container}>
      {isLimitModalOpen && (
        <LimitReachedModal type="creators" onCancel={() => setIsLimitModalOpen(false)} />
      )}

      {isChangeRoleModalOpen && (
        <ChangeRoleModal member={member} onCancel={() => setIsChangeRoleModalOpen(false)} />
      )}

      {isPromoteModalOpen && (
        <PromoteMemberModal
          member={member}
          space={member.space}
          onCancel={() => setIsPromoteModalOpen(false)}
        />
      )}

      {isRemoveModalOpen && (
        <RemoveMemberModal member={member} onCancel={() => setIsRemoveModalOpen(false)} />
      )}

      {isResendDisabled && (
        <span className={styles.coolDown}>
          <TimeIcon /> {timeParser(resendCoolDown)}
        </span>
      )}

      {menuItems.length > 0 && <DropdownMenu items={menuItems} />}
    </div>
  );
});
